
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star } from 'lucide-react';
import { createStoreUrl } from '@/utils/slugUtils';
import type { Profile } from '@/context/AuthContext';

interface StoreCardProps {
  store: Profile;
}

const StoreCard: React.FC<StoreCardProps> = ({ store }) => {
  const navigate = useNavigate();

  const handleVisitStore = () => {
    const storeUrl = createStoreUrl(store.id, store.store_name || store.username || '');
    navigate(storeUrl);
  };

  const storeName = store.store_name || store.username || 'Emri i Dyqanit';
  const storeBanner = store.store_banner_url || '/placeholder.svg';
  const storeLogo = store.profile_image || `https://avatar.vercel.sh/${store.id}`;

  return (
    <Card className="flex flex-col h-full overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl border-transparent rounded-xl bg-white dark:bg-Charcoal-Gray group">
      <CardHeader className="p-0 relative">
        <AspectRatio ratio={16 / 9} className="bg-Neutral-Gray">
          <OptimizedImage
            src={storeBanner}
            alt={`${storeName} banner`}
            className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
            fallbackSrc="/placeholder.svg"
          />
        </AspectRatio>
        <div className="absolute bottom-0 left-4 transform translate-y-1/2">
          <Avatar className="h-20 w-20 border-4 border-white dark:border-Charcoal-Gray shadow-md">
            <AvatarImage src={storeLogo} alt={`${storeName} logo`} />
            <AvatarFallback>{(storeName).charAt(0)}</AvatarFallback>
          </Avatar>
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow p-4 pt-12">
        <h3 className="text-xl font-bold text-Charcoal-Gray dark:text-Pure-White mb-1 truncate" title={storeName}>
          {storeName}
        </h3>
        <div className="flex items-center text-sm text-Neutral-Gray dark:text-Light-Gray mb-2">
          <Star size={16} className="mr-1 text-yellow-400" />
          <span>4.5 (100+ vlerësime)</span> 
        </div>
        {store.store_description && (
          <p className="text-sm text-Medium-Gray dark:text-Light-Purple line-clamp-2 mb-3">
            {store.store_description}
          </p>
        )}
      </CardContent>

      <CardFooter className="p-4 border-t border-Soft-Gray dark:border-Dark-Purple">
        <Button 
          onClick={handleVisitStore} 
          className="w-full bg-Primary-Purple hover:bg-Secondary-Purple text-Pure-White font-semibold py-3"
          variant="default"
          size="lg"
        >
          Vizito Dyqanin
        </Button>
      </CardFooter>
    </Card>
  );
};

export default StoreCard;
